import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { getFleetMarkerIcon, getNumberedStopIcon, getTimelineSpriteIcon } from '../../assets/markers/fleetMarkers';
import { subscribe } from '../SocketIo/Socket';
import VisualTimeline from './VisualTimeline';

const STATUS_CONFIG = {
  ALL: { label: 'Total', color: '#455A64' },
  DRIVING: { label: 'Driving', color: '#1E88E5' },
  ON_SITE: { label: 'On-Site', color: '#2E7D32' },
  AT_TRACKED_LOCATION: { label: 'Tracked', color: '#EF6C00' },
  IDLE_OTHER: { label: 'Idle', color: '#C62828' },
  AT_YARD: { label: 'Yard', color: '#6D6D6D' },
};

const STATUS_ORDER = ['DRIVING', 'ON_SITE', 'AT_TRACKED_LOCATION', 'IDLE_OTHER', 'AT_YARD'];
const DEFAULT_CENTER = [33.4484, -112.0740];
const DEFAULT_ZOOM = 10;
const HYBRID_TILE_URL = 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}';
const ROAD_LABELS_URL = 'https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png';
const HYBRID_ATTRIBUTION = '&copy; Google Maps, &copy; OpenStreetMap contributors &copy; CARTO';

// Bounding box for reasonable operational area — filters out garbage coords (e.g. Madagascar)
const COORD_BOUNDS = { latMin: 24, latMax: 50, lonMin: -125, lonMax: -66 }; // continental US
const isReasonableCoord = (lat, lon) =>
  lat >= COORD_BOUNDS.latMin && lat <= COORD_BOUNDS.latMax &&
  lon >= COORD_BOUNDS.lonMin && lon <= COORD_BOUNDS.lonMax;

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    backgroundColor: '#eee',
    minHeight: '90vh',
    minWidth: '100vw'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  healthDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: theme.spacing(1),
  },
  card: {
    height: '100%',
    border: '1px solid transparent',
  },
  activeCard: {
    border: `1px solid ${theme.palette.primary.main}`,
    boxShadow: theme.shadows[3],
  },
  cardLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  cardValue: {
    fontWeight: 700,
  },
  controls: {
    marginBottom: theme.spacing(2),
  },
  modeSwitch: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  historyFilters: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  searchField: {
    maxWidth: 360,
    width: '100%',
  },
  layoutContainer: {
    height: 'calc(100vh - 270px)',
    minHeight: 560,
  },
  mapPane: {
    height: '100%',
    minHeight: 340,
    overflow: 'hidden',
  },
  mapInner: {
    height: '100%',
  },
  tablePane: {
    height: '100%',
    minHeight: 340,
    display: 'flex',
    flexDirection: 'column',
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto',
  },
  statusChip: {
    color: '#fff',
    fontWeight: 600,
  },
  rowClickable: {
    cursor: 'pointer',
  },
  staleRow: {
    backgroundColor: '#fff8f8',
  },
  discrepancyPanel: {
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1.5),
    border: '1px solid #ffe0b2',
    backgroundColor: '#fff8e1',
  },
  freshnessRail: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  quickActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  drawerPaper: {
    width: 440,
    maxWidth: '100vw',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  mapFallback: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

const formatMinutes = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  const minutes = Number(value);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder > 0 ? `${hours}h ${remainder}m` : `${hours}h`;
};


const formatLastSeen = (value) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes < 0) return 'Unknown';
  if (minutes === 0) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m ago` : `${hours}h ago`;

  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h ago` : `${days}d ago`;
};

const getRecencyStyle = (value) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes < 0) {
    return { backgroundColor: '#B0BEC5', color: '#263238' };
  }
  if (minutes <= 10) {
    return { backgroundColor: '#2E7D32', color: '#fff' };
  }
  if (minutes <= 30) {
    return { backgroundColor: '#EF6C00', color: '#fff' };
  }
  return { backgroundColor: '#C62828', color: '#fff' };
};

const formatDistanceMeters = (value) => {
  const meters = Number(value);
  if (!Number.isFinite(meters) || meters < 0) return null;
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
  return `${Math.round(meters)} m`;
};

const buildNearestContextLabel = (vehicle) => {
  if (!vehicle) return '';
  const trackedDistance = formatDistanceMeters(vehicle.nearestTrackedLocationDistanceMeters);
  const jobsiteDistance = formatDistanceMeters(vehicle.nearestJobsiteDistanceMeters);

  if (vehicle.nearestTrackedLocationName && trackedDistance) {
    return `Nearest tracked: ${vehicle.nearestTrackedLocationName} (${trackedDistance})`;
  }
  if (vehicle.nearestJobsiteName && jobsiteDistance) {
    return `Nearest jobsite: ${vehicle.nearestJobsiteName} (${jobsiteDistance})`;
  }
  return '';
};

const formatDateInput = (value = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dateDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return formatDateInput(d);
};


const getHealthColor = (status) => {
  if (status === 'healthy') return '#2E7D32';
  if (status === 'warning') return '#EF6C00';
  if (status === 'stale') return '#C62828';
  return '#757575';
};

const getStatusColor = (status) => STATUS_CONFIG[status]?.color || '#607D8B';

const sortString = (value) => `${value || ''}`.toLowerCase();

const SEARCH_DEBOUNCE_MS = 300;

const TruckTrackingDashboard = () => {
  const classes = useStyles();
  const baseUrl = `${process.env.REACT_APP_PROTOCOL}://${process.env.REACT_APP_HOST}`;

  const [overview, setOverview] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('live');
  const [activeStatus, setActiveStatus] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [timelineDate, setTimelineDate] = useState(formatDateInput());
  const [pipelineVersion, setPipelineVersion] = useState('v1')
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyCrewId, setHistoryCrewId] = useState('');
  const [historyVehicleId, setHistoryVehicleId] = useState('');
  const [historyStartDate, setHistoryStartDate] = useState(dateDaysAgo(13));
  const [historyEndDate, setHistoryEndDate] = useState(formatDateInput());
  const [expandedHistoryRows, setExpandedHistoryRows] = useState({});
  const [reprocessLoading, setReprocessLoading] = useState(false);
  const [tripBackfillLoading, setTripBackfillLoading] = useState(false);
  const [reprocessNotice, setReprocessNotice] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [timelineStops, setTimelineStops] = useState([]);
  const [highlightedStopIdx, setHighlightedStopIdx] = useState(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterGroupRef = useRef(null);
  const markerMapRef = useRef(new Map()); // vehicleId -> L.Marker
  const stopMarkersRef = useRef([]); // ordered array matching timelineStops indices
  const routeLineRef = useRef(null); // L.polyline for GPS breadcrumb route
  const breadcrumbDotsRef = useRef(null); // L.layerGroup for raw GPS dots on click
  const highlightCircleRef = useRef(null);
  const initialFitDone = useRef(false);
  const popupVehicleRef = useRef(null);
  const mountedRef = useRef(true);
  const pendingTimeoutsRef = useRef([]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      pendingTimeoutsRef.current.forEach(clearTimeout);
      if (timelineAbortRef.current) timelineAbortRef.current.abort();
    };
  }, []);

  const fetchOverview = useCallback(async () => {
    const response = await axios.get(`${baseUrl}/api/fleet/overview`);
    setOverview(response.data || null);
    return response.data;
  }, [baseUrl]);

  const fetchHealth = useCallback(async () => {
    const response = await axios.get(`${baseUrl}/api/fleet/health`);
    setHealth(response.data || null);
    return response.data;
  }, [baseUrl]);

  const fetchDashboardData = useCallback(async ({ withSpinner = false } = {}) => {
    if (withSpinner) setLoading(true);
    setError('');

    try {
      await Promise.all([fetchOverview(), fetchHealth()]);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load fleet dashboard data');
    } finally {
      if (withSpinner) setLoading(false);
      setRefreshing(false);
    }
  }, [fetchHealth, fetchOverview]);

  const timelineAbortRef = useRef(null);
  const fetchTimeline = useCallback(async (vehicleId, dateValue) => {
    if (!vehicleId) return;

    // Cancel any in-flight timeline request
    if (timelineAbortRef.current) timelineAbortRef.current.abort();
    const controller = new AbortController();
    timelineAbortRef.current = controller;

    setTimelineLoading(true);
    setTimelineError('');
    setHighlightedStopIdx(null);
    try {
      const endpoint = pipelineVersion === 'v2'
        ? `${baseUrl}/api/fleet/vehicles/${vehicleId}/timeline-v2`
        : `${baseUrl}/api/fleet/vehicles/${vehicleId}/timeline`
      const response = await axios.get(endpoint, {
        params: { date: dateValue, compare: pipelineVersion === 'v2' ? 'true' : undefined },
        signal: controller.signal,
      });
      setTimelineData(response.data || null);
    } catch (err) {
      if (axios.isCancel(err) || err?.name === 'CanceledError') return; // superseded by newer request
      setTimelineError(err?.response?.data?.error || 'Failed to load timeline');
      setTimelineData(null);
    } finally {
      if (!controller.signal.aborted) setTimelineLoading(false);
    }
  }, [baseUrl, pipelineVersion]);

  useEffect(() => {
    fetchDashboardData({ withSpinner: true });
  }, [fetchDashboardData]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Socket.io — use the project subscribe() pattern (handles Redis adapter + cleanup)
  useEffect(() => {
    let refreshTimeout = null;

    const cleanup = subscribe('fleet:statusUpdate', () => {
      if (refreshTimeout) return;
      refreshTimeout = setTimeout(() => {
        refreshTimeout = null;
        fetchDashboardData();
      }, 800);
    });

    return () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      cleanup();
    };
  }, [fetchDashboardData]);

  // Debounce the search term so map markers don't re-render on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Timeline fetch — only triggered by the effect (not also by handleOpenTimeline)
  useEffect(() => {
    if (!drawerOpen || !selectedVehicle?.id) return;
    fetchTimeline(selectedVehicle.id, timelineDate);
  }, [drawerOpen, selectedVehicle?.id, timelineDate, fetchTimeline, pipelineVersion]);

  const vehicles = overview?.vehicles || [];

  const statusCounts = useMemo(() => ({
    ALL: overview?.totalVehicles || 0,
    DRIVING: overview?.DRIVING || 0,
    ON_SITE: overview?.ON_SITE || 0,
    AT_TRACKED_LOCATION: overview?.AT_TRACKED_LOCATION || 0,
    IDLE_OTHER: overview?.IDLE_OTHER || 0,
    AT_YARD: overview?.AT_YARD || 0,
  }), [overview]);

  const flaggedDiscrepancies = useMemo(() => {
    return vehicles.filter((vehicle) => Boolean(vehicle?.mobileDiscrepancy?.isFlagged));
  }, [vehicles]);

  const crewOptions = useMemo(() => {
    const seen = new Map();
    vehicles.forEach((vehicle) => {
      if (!vehicle?.crew?.id) return;
      if (!seen.has(vehicle.crew.id)) {
        seen.set(vehicle.crew.id, {
          id: vehicle.crew.id,
          name: vehicle.crew.name || 'Unknown Crew',
        });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [vehicles]);

  const vehicleOptions = useMemo(() => {
    return [...vehicles]
      .map(v => ({
        id: v.id,
        name: `${v.name || 'Unknown Vehicle'}${v.number ? ` (#${v.number})` : ''}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [vehicles]);

  const fetchHistoryData = useCallback(async () => {
    if (!historyCrewId && !historyVehicleId) {
      setHistoryError('Select at least a crew or a vehicle to load history.');
      setHistoryRows([]);
      return;
    }

    setHistoryLoading(true);
    setHistoryError('');

    try {
      const params = new URLSearchParams();
      if (historyCrewId) params.append('crewId', historyCrewId);
      if (historyVehicleId) params.append('vehicleId', historyVehicleId);
      if (historyStartDate) params.append('startDate', historyStartDate);
      if (historyEndDate) params.append('endDate', historyEndDate);

      const response = await axios.get(`${baseUrl}/api/fleet/history?${params.toString()}`);
      setHistoryRows(Array.isArray(response.data) ? response.data : []);
      setExpandedHistoryRows({});
    } catch (err) {
      setHistoryError(err?.response?.data?.error || 'Failed to load history');
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [baseUrl, historyCrewId, historyEndDate, historyStartDate, historyVehicleId]);

  const filteredVehicles = useMemo(() => {
    const normalizedSearch = debouncedSearch.trim().toLowerCase();

    let list = [...vehicles];

    if (activeStatus !== 'ALL') {
      list = list.filter(v => v.status === activeStatus);
    }

    if (normalizedSearch) {
      list = list.filter((v) => {
        const vehicleName = sortString(v.name);
        const crewName = sortString(v.crew?.name);
        const locationName = sortString(v.locationName);
        const number = sortString(v.number);
        return vehicleName.includes(normalizedSearch) ||
          crewName.includes(normalizedSearch) ||
          locationName.includes(normalizedSearch) ||
          number.includes(normalizedSearch);
      });
    }

    list.sort((a, b) => {
      let left;
      let right;

      if (sortBy === 'crew') {
        left = sortString(a.crew?.name);
        right = sortString(b.crew?.name);
      } else if (sortBy === 'locationName') {
        left = sortString(a.locationName);
        right = sortString(b.locationName);
      } else if (sortBy === 'currentDurationMinutes') {
        left = Number(a.currentDurationMinutes || 0);
        right = Number(b.currentDurationMinutes || 0);
      } else if (sortBy === 'minutesSinceLastUpdate') {
        left = Number.isFinite(Number(a.minutesSinceLastUpdate)) ? Number(a.minutesSinceLastUpdate) : Number.MAX_SAFE_INTEGER;
        right = Number.isFinite(Number(b.minutesSinceLastUpdate)) ? Number(b.minutesSinceLastUpdate) : Number.MAX_SAFE_INTEGER;
      } else if (sortBy === 'status') {
        left = sortString(a.status);
        right = sortString(b.status);
      } else {
        left = sortString(a[sortBy]);
        right = sortString(b[sortBy]);
      }

      if (left < right) return sortDirection === 'asc' ? -1 : 1;
      if (left > right) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [activeStatus, debouncedSearch, sortBy, sortDirection, vehicles]);

  // ── Leaflet map initialisation (callback ref handles mount/unmount) ──
  const initMap = useCallback((containerEl) => {
    mapContainerRef.current = containerEl;

    // Cleanup previous map if the container was removed
    if (!containerEl) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        clusterGroupRef.current = null;
        markerMapRef.current.clear();
        initialFitDone.current = false;
        setMapReady(false);
      }
      return;
    }

    // Already initialised on this element
    if (mapRef.current) return;

    const map = L.map(containerEl, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: true,
      preferCanvas: true,
    });

    L.tileLayer(HYBRID_TILE_URL, { attribution: HYBRID_ATTRIBUTION, maxZoom: 20 }).addTo(map);
    L.tileLayer(ROAD_LABELS_URL, { maxZoom: 20, pane: 'overlayPane' }).addTo(map);

    const cluster = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 45,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
    });
    map.addLayer(cluster);

    mapRef.current = map;
    clusterGroupRef.current = cluster;
    setMapReady(true);
  }, []);

  // ── Sync markers with filteredVehicles ─────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    const cluster = clusterGroupRef.current;
    if (!map || !cluster) return;

    const existing = markerMapRef.current;
    const currentIds = new Set();

    for (const vehicle of filteredVehicles) {
      const lat = Number(vehicle.lat);
      const lon = Number(vehicle.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      if (!isReasonableCoord(lat, lon)) continue;

      const vid = `${vehicle.id}`;
      currentIds.add(vid);

      if (existing.has(vid)) {
        // Update position + icon of existing marker
        const marker = existing.get(vid);
        marker.setLatLng([lat, lon]);
        marker.setIcon(getFleetMarkerIcon(vehicle));
        marker._vehicleData = vehicle;
      } else {
        // Create new marker
        const marker = L.marker([lat, lon], { icon: getFleetMarkerIcon(vehicle) });
        marker._vehicleData = vehicle;
        marker.on('click', () => {
          setSelectedMarkerId(vid);
          popupVehicleRef.current = vehicle;
        });
        cluster.addLayer(marker);
        existing.set(vid, marker);
      }
    }

    // Remove markers that are no longer in the filtered list
    for (const [vid, marker] of existing) {
      if (!currentIds.has(vid)) {
        cluster.removeLayer(marker);
        existing.delete(vid);
      }
    }

    // Fit bounds once on first data load
    if (!initialFitDone.current && currentIds.size > 0) {
      const bounds = cluster.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40] });
        initialFitDone.current = true;
      }
    }
  }, [mapReady, filteredVehicles]);

  // ── Show popup for selected marker ─────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!selectedMarkerId) {
      map.closePopup();
      return;
    }

    const marker = markerMapRef.current.get(selectedMarkerId);
    if (!marker) return;

    const v = marker._vehicleData;
    if (!v) return;
    const nearestContext = buildNearestContextLabel(v);
    const statusReason = v.statusReason || '';
    const staleText = v.isStale ? 'Yes' : 'No';

    const popupContent = `
      <div style="min-width:200px;font-family:Roboto,sans-serif;font-size:13px;line-height:1.5">
        <strong style="font-size:14px">${v.name || 'Unknown'}</strong><br/>
        <span style="color:#666">Crew:</span> ${v.crew?.name || 'Unassigned'}<br/>
        <span style="color:#666">Status:</span> ${STATUS_CONFIG[v.status]?.label || v.status}<br/>
        <span style="color:#666">Why:</span> ${statusReason || '--'}<br/>
        <span style="color:#666">Location:</span> ${v.locationName || 'Unknown'}<br/>
        ${nearestContext ? `<span style="color:#666">Context:</span> ${nearestContext}<br/>` : ''}
        <span style="color:#666">Duration:</span> ${formatMinutes(v.currentDurationMinutes)}<br/>
        <span style="color:#666">Last seen:</span> ${formatLastSeen(v.minutesSinceLastUpdate)}<br/>
        <span style="color:#666">Stale:</span> ${staleText}<br/>
        <a href="#" class="fleet-popup-timeline" data-vehicle-id="${v.id}"
           style="display:inline-block;margin-top:6px;color:#1E88E5;text-decoration:none;font-weight:500">
          View Timeline
        </a>
      </div>`;

    L.popup({ offset: [0, -16] })
      .setLatLng(marker.getLatLng())
      .setContent(popupContent)
      .openOn(map);
  }, [selectedMarkerId]);

  const handleOpenTimeline = useCallback((vehicle) => {
    if (mapRef.current) mapRef.current.closePopup();
    setSelectedVehicle(vehicle);
    setDrawerOpen(true);
    setSelectedMarkerId(`${vehicle.id}`);
    // fetchTimeline is triggered by the useEffect watching drawerOpen + selectedVehicle
  }, []);

  // ── Handle "View Timeline" clicks inside Leaflet popups ────────────
  useEffect(() => {
    const handler = (e) => {
      const link = e.target.closest('.fleet-popup-timeline');
      if (!link) return;
      e.preventDefault();
      const vid = link.dataset.vehicleId;
      const marker = markerMapRef.current.get(vid);
      if (marker?._vehicleData) {
        handleOpenTimeline(marker._vehicleData);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [handleOpenTimeline]);

  // ── Resize map when drawer open/close changes the container width ───
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const timer = setTimeout(() => map.invalidateSize(), 300);
    return () => clearTimeout(timer);
  }, [drawerOpen, mapReady]);

  // ── Plot numbered stop markers on map when timeline drawer is open ──
  useEffect(() => {
    const map = mapRef.current;
    // Clear previous stop markers
    stopMarkersRef.current.forEach((m) => m && map && map.removeLayer(m));
    stopMarkersRef.current = [];

    if (!map || !drawerOpen || !timelineStops || timelineStops.length === 0) return;

    const STOP_COLORS = {
      JOBSITE: '#2E7D32',
      TRACKED_LOCATION: '#EF6C00',
      YARD: '#6D6D6D',
      IDLE_OTHER: '#C62828',
      CLOCK_IN: '#00897B',
      CLOCK_OUT: '#5E35B1',
      JOB_CHECK_IN: '#1565C0',
      JOB_CHECK_OUT: '#AD1457',
      SCHEDULED_ARRIVAL: '#455A64',
      SCHEDULED_DEPARTURE: '#78909C',
      WORK_TICKET_COMPLETED: '#6A1B9A',
    };
    const MARKER_LABELS = {
      CLOCK_IN: 'CI',
      CLOCK_OUT: 'CO',
      JOB_CHECK_IN: 'JI',
      JOB_CHECK_OUT: 'JO',
      SCHEDULED_ARRIVAL: 'SA',
      SCHEDULED_DEPARTURE: 'SD',
      WORK_TICKET_COMPLETED: 'WC',
    };
    const SPRITE_MARKER_TYPES = new Set([
      'SCHEDULED_ARRIVAL',
      'SCHEDULED_DEPARTURE',
      'WORK_TICKET_COMPLETED',
    ]);

    const bounds = L.latLngBounds([]);
    // Use index-aligned array so highlight lookup matches timelineStops indices
    stopMarkersRef.current = new Array(timelineStops.length).fill(null);
    timelineStops.forEach((stop, stopIdx) => {
      if (stop.lat == null || stop.lon == null) return;
      if (!isReasonableCoord(stop.lat, stop.lon)) return;
      const normalizedMarkerType = `${stop.markerType || ''}`.toUpperCase();
      const color = STOP_COLORS[stop.locationType] || '#607D8B';
      const markerText = stop.isMarker
        ? (MARKER_LABELS[normalizedMarkerType] || 'A')
        : stop.stopNumber;
      const icon = stop.isMarker && SPRITE_MARKER_TYPES.has(normalizedMarkerType)
        ? getTimelineSpriteIcon(normalizedMarkerType)
        : getNumberedStopIcon(markerText, color);
      const marker = L.marker([stop.lat, stop.lon], { icon, zIndexOffset: 1000 })
        .bindTooltip(stop.displayName, { direction: 'top', offset: [0, -10] })
        .addTo(map);
      stopMarkersRef.current[stopIdx] = marker;
      bounds.extend([stop.lat, stop.lon]);
    });

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [mapReady, drawerOpen, timelineStops]);

  // ── Draw GPS breadcrumb route polyline on map ──
  useEffect(() => {
    const map = mapRef.current;

    // Clean up previous route line and breadcrumb dots
    if (routeLineRef.current) {
      if (map) map.removeLayer(routeLineRef.current);
      routeLineRef.current = null;
    }
    if (breadcrumbDotsRef.current) {
      if (map) map.removeLayer(breadcrumbDotsRef.current);
      breadcrumbDotsRef.current = null;
    }

    if (!map || !drawerOpen) return;

    // Prefer road-snapped geometry from OSRM, fall back to raw breadcrumbs
    const hasRoadGeometry = Array.isArray(timelineData?.roadGeometry) && timelineData.roadGeometry.length >= 2;
    const coords = hasRoadGeometry
      ? timelineData.roadGeometry
      : (timelineData?.breadcrumbs || [])
          .filter((p) => p.lat != null && p.lon != null && isReasonableCoord(p.lat, p.lon))
          .map((p) => [p.lat, p.lon]);

    if (coords.length < 2) return;

    const routeLine = L.polyline(coords, {
      color: hasRoadGeometry ? '#1E88E5' : '#90A4AE',
      weight: 4,
      opacity: 0.7,
      smoothFactor: 1,
    }).addTo(map);

    // Click polyline → show nearby raw GPS breadcrumbs
    routeLine.on('click', (e) => {
      // Remove previous dots
      if (breadcrumbDotsRef.current) {
        map.removeLayer(breadcrumbDotsRef.current);
        breadcrumbDotsRef.current = null;
      }

      const rawPoints = (timelineData?.breadcrumbs || [])
        .filter((p) => p.lat != null && p.lon != null && isReasonableCoord(p.lat, p.lon));
      if (rawPoints.length === 0) return;

      const clickLat = e.latlng.lat;
      const clickLon = e.latlng.lng;

      // Show all raw GPS breadcrumbs as small dots with time tooltips
      const dotsLayer = L.layerGroup();
      rawPoints.forEach((p) => {
        const timeStr = p.time ? new Date(p.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '?';
        const speedMph = p.speed != null ? Math.round(p.speed * 0.621371) : null; // Geotab sends km/h
        const speedStr = speedMph != null ? `${speedMph} mph` : '';
        L.circleMarker([p.lat, p.lon], {
          radius: 4,
          color: '#FF5722',
          fillColor: '#FF5722',
          fillOpacity: 0.8,
          weight: 1,
        })
          .bindTooltip(`${timeStr}${speedStr ? ' · ' + speedStr : ''}`, { permanent: false, direction: 'top', offset: [0, -6] })
          .addTo(dotsLayer);
      });

      dotsLayer.addTo(map);
      breadcrumbDotsRef.current = dotsLayer;

      // Zoom to the clicked area
      map.setView(e.latlng, Math.max(map.getZoom(), 15), { animate: true });
    });

    // Render below stop markers
    routeLine.setZIndex && routeLine.setZIndex(400);
    if (routeLine.bringToBack) routeLine.bringToBack();

    routeLineRef.current = routeLine;
  }, [mapReady, drawerOpen, timelineData]);

  // ── Handle timeline entry click → highlight on map ─────────────────
  const handleTimelineEntryClick = useCallback((stopIdx) => {
    setHighlightedStopIdx((prev) => (prev === stopIdx ? null : stopIdx));
  }, []);

  useEffect(() => {
    const map = mapRef.current;

    // Clean up previous highlight
    if (highlightCircleRef.current) {
      map && map.removeLayer(highlightCircleRef.current);
      highlightCircleRef.current = null;
    }

    if (highlightedStopIdx == null || !map) return;

    const marker = stopMarkersRef.current[highlightedStopIdx];
    if (!marker) return;

    const latlng = marker.getLatLng();

    // Pan to the marker
    map.setView(latlng, Math.max(map.getZoom(), 15), { animate: true });

    // Open its tooltip
    marker.openTooltip();

    // Add a pulsing highlight circle
    const circle = L.circleMarker(latlng, {
      radius: 22,
      color: '#1976D2',
      weight: 3,
      opacity: 0.9,
      fillColor: '#1976D2',
      fillOpacity: 0.15,
      className: 'highlight-pulse',
    }).addTo(map);
    highlightCircleRef.current = circle;

    return () => {
      if (highlightCircleRef.current) {
        map.removeLayer(highlightCircleRef.current);
        highlightCircleRef.current = null;
      }
    };
  }, [highlightedStopIdx]);

  // Clear highlight when drawer closes
  useEffect(() => {
    if (!drawerOpen) setHighlightedStopIdx(null);
  }, [drawerOpen]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(field);
    setSortDirection('asc');
  };

  const handleRefresh = () => {
    setRefreshing(true);
    if (viewMode === 'history') {
      fetchHistoryData().finally(() => setRefreshing(false));
      return;
    }
    fetchDashboardData();
  };

  const handleReprocessVisits = async () => {
    const targetVehicleId = viewMode === 'history'
      ? (historyVehicleId || selectedVehicle?.id)
      : (selectedVehicle?.id || historyVehicleId);
    const normalizedTargetId = targetVehicleId ? String(targetVehicleId) : '';

    if (!normalizedTargetId) {
      setReprocessNotice({
        type: 'error',
        message: 'Select a vehicle first (timeline panel or history vehicle filter).',
      });
      return;
    }

    const fallbackVehicle = vehicles.find(v => String(v.id) === normalizedTargetId);
    const targetVehicleName = (selectedVehicle?.id && String(selectedVehicle.id) === normalizedTargetId)
      ? selectedVehicle.name
      : (fallbackVehicle?.name || 'Selected vehicle');
    const targetDate = timelineDate || historyEndDate || formatDateInput();
    const confirmed = window.confirm(
      `Re-sync visit events for ${targetVehicleName} on ${targetDate}? This replaces visit events for that day.`
    );
    if (!confirmed) return;

    setReprocessLoading(true);
    setReprocessNotice(null);

    try {
      // V2 pipeline: use v2-resync endpoint
      if (pipelineVersion === 'v2') {
        const result = await axios.post(`${baseUrl}/api/fleet/v2-resync`, {
          vehicleId: normalizedTargetId,
          date: targetDate,
        });
        if (!mountedRef.current) return;
        const payload = result.data || {};
        setReprocessNotice({
          type: 'success',
          message: `V2 re-sync complete for ${targetVehicleName}: ${payload.trips?.upserted || 0} trips fetched, ${payload.visits?.created || 0} visits created, ${payload.visits?.closed || 0} closed.`,
        });

        await fetchDashboardData();
        if (!mountedRef.current) return;
        if (drawerOpen && selectedVehicle?.id && String(selectedVehicle.id) === normalizedTargetId) {
          await fetchTimeline(normalizedTargetId, timelineDate);
        }
        setReprocessLoading(false);
        return;
      }

      // V1 pipeline: existing reprocess logic
      const [reprocessResult, aspireResult] = await Promise.allSettled([
        axios.post(`${baseUrl}/api/fleet/reprocess-visits`, {
          vehicleId: normalizedTargetId,
          startDate: `${targetDate}T00:00:00.000Z`,
          endDate: `${targetDate}T23:59:59.999Z`,
          dryRun: false,
          replaceExisting: true,
          reclassifyHistory: true,
        }),
        axios.post(`${baseUrl}/api/fleet/backfill-aspire-day`, {
          vehicleId: normalizedTargetId,
          date: targetDate,
          dryRun: false,
          forceRefresh: true,
          backgroundOnMiss: true,
        }),
      ]);

      if (reprocessResult.status !== 'fulfilled') {
        throw reprocessResult.reason;
      }

      const payload = reprocessResult.value?.data || {};
      const aspirePayload = aspireResult.status === 'fulfilled' ? (aspireResult.value?.data || {}) : null;
      const aspireError = aspireResult.status === 'rejected' ? aspireResult.reason : null;
      const updatedHistoryRecords = payload?.historyClassification?.updatedHistoryRecords || 0;
      const reclassAfter = payload?.historyClassification?.after || null;
      const classifiedSummary = reclassAfter
        ? ` Classified points - Yard: ${reclassAfter.YARD || 0}, Jobsite: ${reclassAfter.JOBSITE || 0}, Tracked: ${reclassAfter.TRACKED_LOCATION || 0}, Other: ${reclassAfter.OTHER || 0}.`
        : '';
      const aspireSyncSummary = aspirePayload
        ? ` Aspire: ${aspirePayload.queued ? 'queued' : (aspirePayload.fromCache ? 'cache hit' : 'fetched')} (${aspirePayload.timelineEventsCount || 0} events).`
        : ` Aspire sync error: ${aspireError?.response?.data?.error || aspireError?.message || 'failed'}.`;
      if (!mountedRef.current) return;
      setReprocessNotice({
        type: 'success',
        message: `Re-sync complete for ${targetVehicleName}: inserted ${payload.insertedEvents || 0}, removed ${payload.deletedExisting || 0}, reclassified ${updatedHistoryRecords}.${classifiedSummary}${aspireSyncSummary}`,
      });

      await fetchDashboardData();
      if (!mountedRef.current) return;

      if (drawerOpen && selectedVehicle?.id && String(selectedVehicle.id) === normalizedTargetId) {
        await fetchTimeline(normalizedTargetId, timelineDate);
      }

      if (viewMode === 'history' && (historyCrewId || historyVehicleId)) {
        await fetchHistoryData();
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setReprocessNotice({
        type: 'error',
        message: err?.response?.data?.error || 'Failed to re-sync visit events',
      });
    } finally {
      if (mountedRef.current) setReprocessLoading(false);
    }
  };

  const handleTripBackfillDay = async () => {
    const targetVehicleId = viewMode === 'history'
      ? (historyVehicleId || selectedVehicle?.id)
      : (selectedVehicle?.id || historyVehicleId);
    const normalizedTargetId = targetVehicleId ? String(targetVehicleId) : '';

    if (!normalizedTargetId) {
      setReprocessNotice({
        type: 'error',
        message: 'Select a vehicle first (timeline panel or history vehicle filter).',
      });
      return;
    }

    const fallbackVehicle = vehicles.find(v => String(v.id) === normalizedTargetId);
    const targetVehicleName = (selectedVehicle?.id && String(selectedVehicle.id) === normalizedTargetId)
      ? selectedVehicle.name
      : (fallbackVehicle?.name || 'Selected vehicle');
    const targetDate = timelineDate || historyEndDate || formatDateInput();

    const confirmed = window.confirm(
      `Backfill ${targetVehicleName} for ${targetDate} from Geotab + Aspire? This caches historical days and only refreshes today's data by TTL.`
    );
    if (!confirmed) return;

    setTripBackfillLoading(true);
    setReprocessNotice(null);

    try {
      const [tripResult, aspireResult] = await Promise.allSettled([
        axios.post(`${baseUrl}/api/fleet/backfill-trip-day`, {
          vehicleId: normalizedTargetId,
          date: targetDate,
          dryRun: false,
          forceRefresh: true,
          backgroundOnMiss: true,
        }),
        axios.post(`${baseUrl}/api/fleet/backfill-aspire-day`, {
          vehicleId: normalizedTargetId,
          date: targetDate,
          dryRun: false,
          forceRefresh: true,
          backgroundOnMiss: true,
        }),
      ]);

      const tripError = tripResult.status === 'rejected' ? tripResult.reason : null;
      const aspireError = aspireResult.status === 'rejected' ? aspireResult.reason : null;
      const tripPayload = tripResult.status === 'fulfilled' ? (tripResult.value?.data || {}) : null;
      const aspirePayload = aspireResult.status === 'fulfilled' ? (aspireResult.value?.data || {}) : null;

      if (!tripPayload && !aspirePayload) {
        throw tripError || aspireError || new Error('Both backfill requests failed');
      }

      if (!mountedRef.current) return;

      const tripLabel = tripPayload
        ? (tripPayload.queued ? 'queued' : (tripPayload.fromCache ? 'cache hit' : 'fetched'))
        : `error: ${tripError?.response?.data?.error || tripError?.message || 'failed'}`
      const aspireLabel = aspirePayload
        ? (aspirePayload.queued ? 'queued' : (aspirePayload.fromCache ? 'cache hit' : 'fetched'))
        : `error: ${aspireError?.response?.data?.error || aspireError?.message || 'failed'}`

      const tripEventsCount = tripPayload?.timelineEventsCount || 0;
      const aspireEventsCount = aspirePayload?.timelineEventsCount || 0;
      const queued = Boolean(tripPayload?.queued || aspirePayload?.queued);
      const noticeMessage = queued
        ? `Backfill queued for ${targetVehicleName} on ${targetDate}. Trip: ${tripLabel} (${tripEventsCount} events). Aspire: ${aspireLabel} (${aspireEventsCount} events). Refreshing timeline shortly...`
        : `Backfill complete for ${targetVehicleName} on ${targetDate}. Trip: ${tripLabel} (${tripEventsCount} events). Aspire: ${aspireLabel} (${aspireEventsCount} events).`;

      setReprocessNotice({
        type: 'success',
        message: noticeMessage,
      });

      if (drawerOpen && selectedVehicle?.id && String(selectedVehicle.id) === normalizedTargetId) {
        if (queued) {
          const tid = setTimeout(() => {
            if (mountedRef.current) fetchTimeline(normalizedTargetId, targetDate);
          }, 5000);
          pendingTimeoutsRef.current.push(tid);
        } else {
          await fetchTimeline(normalizedTargetId, targetDate);
        }
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setReprocessNotice({
        type: 'error',
        message: err?.response?.data?.error || err?.message || 'Failed to backfill selected day',
      });
    } finally {
      if (mountedRef.current) setTripBackfillLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode !== 'history') return;
    if (historyCrewId || historyVehicleId) return;
    if (crewOptions.length > 0) {
      setHistoryCrewId(crewOptions[0].id);
    } else if (vehicleOptions.length > 0) {
      setHistoryVehicleId(vehicleOptions[0].id);
    }
  }, [crewOptions, historyCrewId, historyVehicleId, vehicleOptions, viewMode]);

  const historyRowKey = (row) => `${row.date || 'unknown'}|${row.vehicle?.id || row.vehicle?.name || 'vehicle'}`;
  const toggleHistoryRow = (row) => {
    const key = historyRowKey(row);
    setExpandedHistoryRows(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const timelineFreshnessChips = useMemo(() => {
    const freshness = timelineData?.timelineFreshness;
    if (!freshness) return [];

    const chips = [];
    const gpsMinutes = freshness?.gps?.minutesSinceLastPoint;
    chips.push({
      key: 'gps',
      label: `GPS ${gpsMinutes === null || gpsMinutes === undefined ? 'unknown' : `${gpsMinutes}m`}`,
      tone: freshness?.gps?.status === 'stale' ? '#C62828' : '#2E7D32',
      tooltip: freshness?.gps?.lastPointAt
        ? `Last GPS point ${new Date(freshness.gps.lastPointAt).toLocaleString()}`
        : 'No GPS points for this day',
    });

    const tripCacheAge = freshness?.tripBackfillCache?.cacheAgeMinutes;
    chips.push({
      key: 'trip-cache',
      label: `Trip cache ${tripCacheAge === null || tripCacheAge === undefined ? 'n/a' : `${tripCacheAge}m`}`,
      tone: freshness?.tripBackfillCache?.available ? '#1565C0' : '#78909C',
      tooltip: freshness?.tripBackfillCache?.lastFetchedAt
        ? `Trip cache fetched ${new Date(freshness.tripBackfillCache.lastFetchedAt).toLocaleString()}`
        : 'No trip cache for this day',
    });

    const aspireCacheAge = freshness?.aspireDayCache?.cacheAgeMinutes;
    chips.push({
      key: 'aspire-cache',
      label: `Aspire cache ${aspireCacheAge === null || aspireCacheAge === undefined ? 'n/a' : `${aspireCacheAge}m`}`,
      tone: freshness?.aspireDayCache?.available ? '#6A1B9A' : '#78909C',
      tooltip: freshness?.aspireDayCache?.lastFetchedAt
        ? `Aspire cache fetched ${new Date(freshness.aspireDayCache.lastFetchedAt).toLocaleString()}`
        : 'No Aspire cache for this day',
    });

    return chips;
  }, [timelineData]);

  if (loading) {
    return (
      <Container className={classes.root}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={380}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.titleWrap}>
          <Typography variant="h4">Fleet Tracking Dashboard</Typography>
          <Chip
            size="small"
            label={viewMode === 'live'
              ? (activeStatus === 'ALL' ? 'All Vehicles' : STATUS_CONFIG[activeStatus]?.label || activeStatus)
              : 'History'}
            style={{ backgroundColor: '#ECEFF1' }}
          />
        </Box>

        <Box display="flex" alignItems="center" gridGap={12}>
          <Tooltip title={health?.lastSync ? `Last sync ${new Date(health.lastSync).toLocaleString()}` : 'No sync data'}>
            <Chip
              label={health ? `${health.status || 'unknown'}${health?.minutesSinceSync !== null && health?.minutesSinceSync !== undefined ? ` (${health.minutesSinceSync}m)` : ''}` : 'Health: unknown'}
              icon={<span className={classes.healthDot} style={{ backgroundColor: getHealthColor(health?.status) }} />}
              variant="outlined"
            />
          </Tooltip>

          <Tooltip
            title={selectedVehicle?.id || historyVehicleId
              ? 'Rebuild visit events from Geotab history for the selected day and sync Aspire day markers'
              : 'Select a vehicle first'}
          >
            <span>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleReprocessVisits}
                disabled={reprocessLoading || (!selectedVehicle?.id && !historyVehicleId)}
                startIcon={reprocessLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {reprocessLoading ? 'Re-syncing...' : 'Re-sync Visits (Day)'}
              </Button>
            </span>
          </Tooltip>

          <Tooltip
            title={selectedVehicle?.id || historyVehicleId
              ? 'Cache-first day backfill (Geotab + Aspire). Historical dates are reused from cache.'
              : 'Select a vehicle first'}
          >
            <span>
              <Button
                variant="outlined"
                color="default"
                onClick={handleTripBackfillDay}
                disabled={tripBackfillLoading || reprocessLoading || (!selectedVehicle?.id && !historyVehicleId)}
                startIcon={tripBackfillLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {tripBackfillLoading ? 'Backfilling...' : 'Day Backfill (Trip + Aspire)'}
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            color="primary"
            onClick={handleRefresh}
            disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? (
        <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: '#ffebee' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : null}

      {reprocessNotice ? (
        <Paper style={{ padding: 12, marginBottom: 12, backgroundColor: reprocessNotice.type === 'error' ? '#ffebee' : '#e8f5e9' }}>
          <Typography color={reprocessNotice.type === 'error' ? 'error' : 'primary'} variant="body2">
            {reprocessNotice.message}
          </Typography>
        </Paper>
      ) : null}

      {viewMode === 'live' && flaggedDiscrepancies.length > 0 ? (
        <Paper className={classes.discrepancyPanel}>
          <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
            Mobile vs Vehicle Discrepancies: {flaggedDiscrepancies.length}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {flaggedDiscrepancies
              .slice(0, 4)
              .map((vehicle) => {
                const meters = vehicle?.mobileDiscrepancy?.distanceVehicleToMobileMeters;
                const formatted = formatDistanceMeters(meters) || '--';
                return `${vehicle.name || 'Vehicle'} (${formatted})`;
              })
              .join(' • ')}
            {flaggedDiscrepancies.length > 4 ? ' • ...' : ''}
          </Typography>
        </Paper>
      ) : null}

      <Box className={classes.modeSwitch}>
        <Button
          variant={viewMode === 'live' ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => setViewMode('live')}
        >
          Live
        </Button>
        <Button
          variant={viewMode === 'history' ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => setViewMode('history')}
        >
          History
        </Button>
      </Box>

      {viewMode === 'live' ? (
        <>
          <Grid container spacing={2} style={{ marginBottom: 8 }}>
            {['ALL', ...STATUS_ORDER].map((statusKey) => (
              <Grid item xs={6} sm={4} md={2} key={statusKey}>
                <Card className={`${classes.card} ${activeStatus === statusKey ? classes.activeCard : ''}`}>
                  <CardActionArea onClick={() => setActiveStatus(prev => (prev === statusKey ? 'ALL' : statusKey))}>
                    <CardContent>
                      <Typography className={classes.cardLabel}>{STATUS_CONFIG[statusKey].label}</Typography>
                      <Typography variant="h4" className={classes.cardValue} style={{ color: STATUS_CONFIG[statusKey].color }}>
                        {statusCounts[statusKey]}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box className={classes.controls} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gridGap={12}>
            <TextField
              className={classes.searchField}
              variant="outlined"
              size="small"
              placeholder="Search vehicle, crew, location..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="body2" color="textSecondary">
              Showing {filteredVehicles.length} of {vehicles.length} vehicles
              {lastUpdated ? ` • Updated ${lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}` : ''}
            </Typography>
          </Box>

          <Grid container spacing={2} className={classes.layoutContainer}>
            <Grid item xs={12} md={drawerOpen ? 12 : 7} className={classes.mapPane}>
              <Paper className={classes.mapInner}>
                <div ref={initMap} style={{ width: '100%', height: '100%' }} />
              </Paper>
            </Grid>

            {!drawerOpen && <Grid item xs={12} md={5} className={classes.tablePane}>
              <Paper className={classes.tablePane}>
                <TableContainer className={classes.tableContainer}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={sortBy === 'name' ? sortDirection : false}>
                          <TableSortLabel active={sortBy === 'name'} direction={sortDirection} onClick={() => handleSort('name')}>
                            Vehicle
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'crew' ? sortDirection : false}>
                          <TableSortLabel active={sortBy === 'crew'} direction={sortDirection} onClick={() => handleSort('crew')}>
                            Crew
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'status' ? sortDirection : false}>
                          <TableSortLabel active={sortBy === 'status'} direction={sortDirection} onClick={() => handleSort('status')}>
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'locationName' ? sortDirection : false}>
                          <TableSortLabel active={sortBy === 'locationName'} direction={sortDirection} onClick={() => handleSort('locationName')}>
                            Location
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={sortBy === 'currentDurationMinutes' ? sortDirection : false}>
                          <TableSortLabel active={sortBy === 'currentDurationMinutes'} direction={sortDirection} onClick={() => handleSort('currentDurationMinutes')}>
                            Duration
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={sortBy === 'minutesSinceLastUpdate' ? sortDirection : false}>
                          <TableSortLabel active={sortBy === 'minutesSinceLastUpdate'} direction={sortDirection} onClick={() => handleSort('minutesSinceLastUpdate')}>
                            Last Seen
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredVehicles.map((vehicle) => (
                        <TableRow
                          key={vehicle.id}
                          hover
                          className={`${classes.rowClickable} ${vehicle.isStale ? classes.staleRow : ''}`}
                          onClick={() => handleOpenTimeline(vehicle)}
                        >
                          <TableCell>{vehicle.name || '--'}</TableCell>
                          <TableCell>{vehicle.crew?.name || '--'}</TableCell>
                          <TableCell>
                            <Tooltip title={vehicle.statusReason || 'No status explanation available'}>
                              <Chip
                                label={STATUS_CONFIG[vehicle.status]?.label || vehicle.status || '--'}
                                size="small"
                                className={classes.statusChip}
                                style={{ backgroundColor: getStatusColor(vehicle.status) }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={buildNearestContextLabel(vehicle) || 'No nearest context available'}>
                              <span>{vehicle.locationName || '--'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{formatMinutes(vehicle.currentDurationMinutes)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title={vehicle.lastUpdate ? new Date(vehicle.lastUpdate).toLocaleString() : 'No location timestamp available'}>
                              <Chip
                                size="small"
                                label={formatLastSeen(vehicle.minutesSinceLastUpdate)}
                                style={getRecencyStyle(vehicle.minutesSinceLastUpdate)}
                              />
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {!filteredVehicles.length ? (
                  <Box className={classes.emptyState}>
                    <Typography variant="body2">No vehicles match the current filters.</Typography>
                  </Box>
                ) : null}
              </Paper>
            </Grid>}
          </Grid>
        </>
      ) : (
        <>
          <Paper className={classes.historyFilters}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Crew"
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={historyCrewId}
                  onChange={(event) => setHistoryCrewId(event.target.value)}
                >
                  <MenuItem value="">Any Crew</MenuItem>
                  {crewOptions.map((crew) => (
                    <MenuItem key={crew.id} value={crew.id}>
                      {crew.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Vehicle"
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={historyVehicleId}
                  onChange={(event) => setHistoryVehicleId(event.target.value)}
                >
                  <MenuItem value="">Any Vehicle</MenuItem>
                  {vehicleOptions.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={historyStartDate}
                  onChange={(event) => setHistoryStartDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={historyEndDate}
                  onChange={(event) => setHistoryEndDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Box display="flex" gridGap={8}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={fetchHistoryData}
                    disabled={historyLoading || (!historyCrewId && !historyVehicleId)}
                    fullWidth
                  >
                    {historyLoading ? 'Loading...' : 'Load'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Box mt={1}>
              <Typography variant="caption" color="textSecondary">
                Choose at least a crew or vehicle. Date range uses UTC calendar days.
              </Typography>
            </Box>
          </Paper>

          {historyError ? (
            <Paper style={{ padding: 12, marginBottom: 12, backgroundColor: '#ffebee' }}>
              <Typography color="error" variant="body2">{historyError}</Typography>
            </Paper>
          ) : null}

          <Paper>
            <TableContainer style={{ maxHeight: 'calc(100vh - 320px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={48} />
                    <TableCell>Date</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell align="right">Driving</TableCell>
                    <TableCell align="right">On-Site</TableCell>
                    <TableCell align="right">Tracked</TableCell>
                    <TableCell align="right">Idle</TableCell>
                    <TableCell align="right">Yard</TableCell>
                    <TableCell align="right">Events</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyRows.map((row) => {
                    const rowKey = historyRowKey(row);
                    const expanded = !!expandedHistoryRows[rowKey];
                    return (
                      <React.Fragment key={rowKey}>
                        <TableRow hover className={classes.rowClickable} onClick={() => toggleHistoryRow(row)}>
                          <TableCell>
                            <IconButton size="small">
                              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{row.date || '--'}</TableCell>
                          <TableCell>{row.vehicle?.name || '--'}</TableCell>
                          <TableCell align="right">{formatMinutes(row.summary?.drivingMinutes)}</TableCell>
                          <TableCell align="right">{formatMinutes(row.summary?.onSiteMinutes)}</TableCell>
                          <TableCell align="right">{formatMinutes(row.summary?.trackedLocationMinutes)}</TableCell>
                          <TableCell align="right">{formatMinutes(row.summary?.idleOtherMinutes)}</TableCell>
                          <TableCell align="right">{formatMinutes(row.summary?.atYardMinutes)}</TableCell>
                          <TableCell align="right">{row.events?.length || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Box p={2}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Timeline • {row.vehicle?.name || '--'} • {row.date || '--'}
                                </Typography>
                                <VisualTimeline
                                  events={row.events || []}
                                  summary={row.summary || null}
                                  compact
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {!historyLoading && historyRows.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography variant="body2">
                  Load history with a crew/vehicle and date range to view results.
                </Typography>
              </Box>
            ) : null}
            {historyLoading ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={26} />
              </Box>
            ) : null}
          </Paper>
        </>
      )}

      <Drawer
        anchor="right"
        variant="persistent"
        open={drawerOpen}
        classes={{ paper: classes.drawerPaper }}
      >
        <Box className={classes.drawerHeader}>
          <Typography variant="h6">
            {selectedVehicle?.name || 'Vehicle Timeline'}
          </Typography>
          <IconButton size="small" onClick={() => setDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="textSecondary">
          Crew: {selectedVehicle?.crew?.name || 'Unassigned'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Status: {STATUS_CONFIG[selectedVehicle?.status]?.label || selectedVehicle?.status || '--'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Why: {selectedVehicle?.statusReason || '--'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Last seen: {formatLastSeen(selectedVehicle?.minutesSinceLastUpdate)}
        </Typography>
        {selectedVehicle ? (
          <Typography variant="body2" color="textSecondary">
            Context: {buildNearestContextLabel(selectedVehicle) || '--'}
          </Typography>
        ) : null}
        {selectedVehicle?.mobileDiscrepancy?.isFlagged ? (
          <Typography variant="body2" style={{ color: '#C62828' }}>
            Mobile discrepancy: {formatDistanceMeters(selectedVehicle.mobileDiscrepancy.distanceVehicleToMobileMeters) || '--'}
          </Typography>
        ) : null}

        <Box mt={2} mb={1}>
          <TextField
            label="Date"
            type="date"
            value={timelineDate}
            onChange={(event) => setTimelineDate(event.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button
            size="small"
            variant={pipelineVersion === 'v1' ? 'contained' : 'outlined'}
            onClick={() => setPipelineVersion('v1')}
          >
            V1
          </Button>
          <Button
            size="small"
            variant={pipelineVersion === 'v2' ? 'contained' : 'outlined'}
            onClick={() => setPipelineVersion('v2')}
          >
            V2
          </Button>
        </Box>
        {pipelineVersion === 'v2' && timelineData?.v1Comparison && (
          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
            v1: {timelineData.v1Comparison.events?.filter(e => e.type === 'ARRIVAL').length || 0} stops |
            v2: {timelineData.events?.filter(e => e.type === 'ARRIVAL').length || 0} stops
          </Box>
        )}

        <Box className={classes.quickActions}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleReprocessVisits}
            disabled={reprocessLoading || !selectedVehicle?.id}
          >
            {reprocessLoading ? 'Re-syncing...' : 'Re-sync Day'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleTripBackfillDay}
            disabled={tripBackfillLoading || reprocessLoading || !selectedVehicle?.id}
          >
            {tripBackfillLoading ? 'Backfilling...' : 'Backfill Day'}
          </Button>
        </Box>

        {timelineFreshnessChips.length > 0 ? (
          <Box className={classes.freshnessRail}>
            {timelineFreshnessChips.map((chip) => (
              <Tooltip key={chip.key} title={chip.tooltip || ''}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={chip.label}
                  style={{ borderColor: chip.tone, color: chip.tone }}
                />
              </Tooltip>
            ))}
          </Box>
        ) : null}

        <Divider style={{ marginBottom: 12 }} />

        <Box style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {timelineLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={26} />
            </Box>
          ) : null}

          {timelineError ? (
            <Paper style={{ padding: 12, backgroundColor: '#ffebee', marginBottom: 12 }}>
              <Typography color="error" variant="body2">{timelineError}</Typography>
            </Paper>
          ) : null}

          <VisualTimeline
            events={timelineData?.events || []}
            summary={timelineData?.summary || null}
            expectedSchedule={timelineData?.expectedSchedule || null}
            onResync={selectedVehicle?.id ? handleReprocessVisits : undefined}
            resyncLoading={reprocessLoading}
            onStopsChange={setTimelineStops}
            onEntryClick={handleTimelineEntryClick}
            highlightedStopIdx={highlightedStopIdx}
          />
        </Box>
      </Drawer>
    </Container>
  );
};

export default TruckTrackingDashboard;
